import {
  CnpjFormField,
  FloatingFormItem,
  InputDecimal,
  InputText,
} from '@/components/_common';
import CfopSelectField from '@/components/_common/CfopSelectField';
import NcmSelectField from '@/components/_common/NcmSelectField';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Card, Col, Form, FormInstance, Input, Row, Select } from 'antd';
import { InvoicePartFormValues } from '../InvoicePartForm.types';
import { generalScaleIndicatorOptions } from './options';
import CestFormField from '@/components/_common/CestFormField';
import TextArea from 'antd/lib/input/TextArea';
import { useInvoicePartNcmChangeFillFieds } from '../InvoicePart.hooks';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import { useNcmMutation } from '@/core/domain/operational/Ncm/Ncm.hooks';
import { useInvoiceFormContext } from '../../../InvoiceFormProvider';

interface Props {
  isOriginPurchase: boolean;
  partHasBarCode: boolean;
  form: FormInstance<InvoicePartFormValues>;
  invoiceItemId?: number;
  companyId?: number;
  invoiceType?: InvoiceType;
}

const InvoicePartGeneralDataForm: React.FC<Props> = (props) => {
  const {
    isOriginPurchase,
    partHasBarCode,
    form,
    invoiceItemId,
    companyId,
    invoiceType,
  } = props;

  const { disabledFields, invoiceSettingNfe } = useInvoiceFormContext();

  const [ncmChangeFillFields, ncmChangeFillFieldsLoading] =
    useInvoicePartNcmChangeFillFieds({
      companyId,
    });

  const partId = Form.useWatch(['general', 'partId'], form);
  const additionalInformation = Form.useWatch(
    ['general', 'additionalInformation'],
    form,
  );

  const ncmDetail = useNcmMutation();

  const required = useDefaultEmptyFieldError();
  const defaultMaxLengthFieldError = useDefaultMaxLengthFieldError();

  const handleUpdateApproximateTaxValue = async (valueTotal: number) => {
    const ncmId = form.getFieldValue(['general', 'ncmId']);

    const ncm = await ncmDetail.mutateAsync(ncmId);

    form.setFieldValue(
      ['general', 'approximateTaxValue'],
      ((valueTotal || 0) *
        (ncm?.taxesPercentage || invoiceSettingNfe?.generalIbptRate || 0)) /
        100,
    );
  };

  const handleValueTotal = (key: string, value: number) => {
    const generalValues = form.getFieldValue(['general']);

    const quantity = key === 'quantity' ? value : generalValues?.quantity;
    const valueUnitary =
      key === 'valueUnitary' ? value : generalValues?.valueUnitary;
    const freightValue =
      key === 'freightValue' ? value : generalValues?.freightValue;
    const insuranceValue =
      key === 'insuranceValue' ? value : generalValues?.insuranceValue;
    const otherValue = key === 'otherValue' ? value : generalValues?.otherValue;
    const valueDiscount =
      key === 'valueDiscount' ? value : generalValues?.valueDiscount;

    const valueTotal =
      (quantity || 0) * (valueUnitary || 0) +
      (freightValue || 0) +
      (insuranceValue || 0) +
      (otherValue || 0) -
      (valueDiscount || 0);

    form.setFieldValue(['general', 'valueTotal'], valueTotal);

    handleUpdateApproximateTaxValue(valueTotal);
  };

  return (
    <>
      {ncmChangeFillFieldsLoading && (
        <ModalLoadingOverlay
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backdropFilter: 'blur(1px)',
          }}
          messages={['Preenchendo dados da peça e tributação...']}
        />
      )}

      <Form form={form} preserve={false} disabled={disabledFields}>
        <Form.Item name={['general', 'partId']} hidden />
        <Form.Item name={['general', 'consumableId']} hidden />

        <Card title="Detalhes" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            <Col span={24} lg={12}>
              <FloatingFormItem
                name={['general', 'description']}
                label="Descrição"
                rules={[required]}
              >
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={10} lg={5}>
              <FloatingFormItem
                name={['general', 'ncmId']}
                label="NCM"
                rules={[required]}
                getValueFromEvent={(value: number) => {
                  (async () => {
                    if (value) {
                      const fields = await ncmChangeFillFields(
                        invoiceItemId,
                        value,
                        invoiceType,
                      );

                      form.setFieldsValue(fields);

                      handleUpdateApproximateTaxValue(
                        form.getFieldValue(['general', 'valueTotal']),
                      );
                    }
                  })();

                  return value;
                }}
              >
                <NcmSelectField
                  queryFilter={{ active: true }}
                  disabled={!hasPermissions([Authorities.REGISTER_NCM_VIEW])}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={6} lg={3}>
              <FloatingFormItem
                name={['general', 'unitMeasurement']}
                label="Unidade"
                rules={[required]}
              >
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8} lg={4}>
              <CestFormField
                fieldName={['general', 'cest']}
                label="CEST"
                required={false}
              />
            </Col>

            <Col span={24}>
              <FloatingFormItem
                name={['general', 'cfopId']}
                label="CFOP"
                rules={[required]}
              >
                <CfopSelectField />
              </FloatingFormItem>
            </Col>

            {!partId && (
              <Col span={24} sm={12}>
                <FloatingFormItem
                  name={['general', 'barCode']}
                  label="Cód. Barras"
                >
                  <Input disabled={partHasBarCode} />
                </FloatingFormItem>
              </Col>
            )}

            <Col span={24} sm={12}>
              <CnpjFormField
                disabled={disabledFields}
                fieldName={['general', 'manufacturerMerchandiseCnpj']}
                label="CNPJ do fabricante"
                notRequired
              />
            </Col>

            <Col span={24} sm={12}>
              <FloatingFormItem
                name={['general', 'taxBenefitCode']}
                label="Cód. benefício fiscal"
              >
                <Input />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>

        <Card title="Valores" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            <Col span={24} sm={12} md={8} lg={6}>
              <FloatingFormItem
                name={['general', 'quantity']}
                label="Quantidade"
                rules={[required]}
                initialValue={1}
                getValueFromEvent={(value) => {
                  handleValueTotal('quantity', value);
                  return value;
                }}
              >
                <InputDecimal disabled={isOriginPurchase} />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8} lg={6}>
              <FloatingFormItem
                name={['general', 'valueUnitary']}
                label="Valor unitário"
                rules={[required]}
                getValueFromEvent={(value) => {
                  handleValueTotal('valueUnitary', value);
                  return value;
                }}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8} lg={6}>
              <FloatingFormItem
                name={['general', 'valueDiscount']}
                label="Valor de desconto"
                initialValue={0}
                getValueFromEvent={(value) => {
                  handleValueTotal('valueDiscount', value);
                  return value;
                }}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8} lg={6}>
              <FloatingFormItem
                name={['general', 'freightValue']}
                label="Valor de frete"
                getValueFromEvent={(value) => {
                  handleValueTotal('freightValue', value);
                  return value;
                }}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8} lg={6}>
              <FloatingFormItem
                name={['general', 'insuranceValue']}
                label="Valor do seguro"
                getValueFromEvent={(value) => {
                  handleValueTotal('insuranceValue', value);
                  return value;
                }}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8} lg={6}>
              <FloatingFormItem
                name={['general', 'otherValue']}
                label="Valor de despesa"
                initialValue={0}
                getValueFromEvent={(value) => {
                  handleValueTotal('otherValue', value);
                  return value;
                }}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8} lg={6}>
              <FloatingFormItem
                name={['general', 'approximateTaxValue']}
                label="Valor aproximado total de tributos"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8} lg={6}>
              <FloatingFormItem
                name={['general', 'valueTotal']}
                label="Valor total"
                initialValue={0}
              >
                <InputDecimal disabled />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>

        {isOriginPurchase && (
          <Card
            title="Documento referenciado"
            type="inner"
            style={{ marginBottom: 24 }}
          >
            <Row gutter={[24, 16]}>
              <Col span={24} lg={12}>
                <FloatingFormItem
                  name={['general', 'keyNfePurchase']}
                  label="Chave de acesso do doc. referenciado"
                >
                  <Input />
                </FloatingFormItem>
              </Col>

              <Col span={24} lg={12}>
                <FloatingFormItem
                  name={['general', 'itemNumberPurchase']}
                  label="Número do item do doc. referenciado"
                >
                  <Input />
                </FloatingFormItem>
              </Col>
            </Row>
          </Card>
        )}

        <Card title="Complemento" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            <Col span={24} sm={12} lg={8}>
              <FloatingFormItem
                name={['general', 'relevanceScaleIndicator']}
                label="Indicador de escala relevante"
              >
                <Select options={generalScaleIndicatorOptions} />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} lg={8}>
              <FloatingFormItem
                name={['general', 'purchaseNumber']}
                label="Número do pedido da compra"
                rules={[defaultMaxLengthFieldError(15)]}
              >
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8}>
              <FloatingFormItem
                name={['general', 'purchaseOrder']}
                label="Item do pedido da compra"
                rules={[defaultMaxLengthFieldError(6)]}
              >
                <InputText onlyNumber />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8}>
              <FloatingFormItem name={['general', 'nve']} label="NVE">
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} md={8}>
              <FloatingFormItem name={['general', 'exTipi']} label="Ex Tipi">
                <Input />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>

        <Card
          title={`Informações Adicionais do Produto - ${
            500 - Number(additionalInformation?.length || 0)
          } caracteres restantes`}
          type="inner"
        >
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <FloatingFormItem
                name={['general', 'additionalInformation']}
                label="Informações Adicionais do Produto"
              >
                <TextArea disabled={disabledFields} maxLength={500} rows={5} />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>
      </Form>
    </>
  );
};

export default InvoicePartGeneralDataForm;
